import { Auth } from "aws-amplify";
import { PhoneDetails } from "../phone-verification/phone-verification";

type User = {
    username: string;
    password: string;
    telephone?: PhoneDetails;
    nickname?: string;
    created?: number;
    email?: { address: string };
};

type UserAttributes = {
    phone_number: string;
    nickname: string;
    "custom:created": string;
};

/**
 * Create new user account (should send phone verification code)
 * @param {User | undefined} user
 */
export const signUp = async (user: User) => {
    const { username, password, telephone, nickname, created } = user;

    const attributes = {
        nickname: `${nickname}`,
        "custom:created": `${created}`
    } as UserAttributes;

    if (telephone && telephone.international !== undefined) {
        attributes.phone_number = telephone.international;
    }

    return await Auth.signUp({
        username,
        password,
        attributes: attributes
    });
};

export const getCurrentUser = async () => {
    const user = await Auth.currentUserInfo();

    return user;
};

export const getCurrentAuthenticatedUser = async () => {
    const user = await Auth.currentAuthenticatedUser();
    return user;
};

/**
 * Resend telephone verification code
 * @param {User | undefined} user
 */
export const resendSignUp = async (username: string) => {
    return await Auth.resendSignUp(username);
};

/**
 * Confirm telephone verification code
 * @param {string} username
 * @param {string} verificationCode
 */
export const confirmVerification = async (
    username: string,
    verificationCode: string
) => {
    return await Auth.confirmSignUp(username, verificationCode);
};

/**
 * Sign in user
 * @param {User | undefined} user
 */
export const signIn = async (user: User) => {
    const { username, password } = user;
    return await Auth.signIn(username, password);
};

/**
 * Update user phone number
 * @param {string} phoneNumber
 */
export const updateUserPhoneNumber = async (phone_number: string) => {
    const session = await Auth.currentAuthenticatedUser();
    return await Auth.updateUserAttributes(session, { phone_number });
};

/**
 * Update user email address
 * @param {string} email
 */
export const updateUserEmail = async (email: string) => {
    const session = await Auth.currentAuthenticatedUser();
    return await Auth.updateUserAttributes(session, { email });
};

/**
 * Send email verification
 */
export const sendEmailVerification = async () => {
    return Auth.verifyCurrentUserAttribute("email");
};

/**
 * Send phoneNumber verification
 */
export const sendPhoneNumberVerification = async () => {
    return Auth.verifyCurrentUserAttribute("phone_number");
};

/**
 *
 * @param {string} verification
 */
export const confirmEmailVerification = async (verification: string) => {
    return await Auth.verifyCurrentUserAttributeSubmit("email", verification);
};

/**
 *
 * @param {string} verification
 */
export const confirmPhoneNumberVerification = async (verification: string) => {
    return await Auth.verifyCurrentUserAttributeSubmit(
        "phone_number",
        verification
    );
};

/**
 * 
 * @param {boolean} global 
 */
export const signOut = async (global: boolean) => {
    return await Auth.signOut({
        global: global
    });
};